import React from "react";

import { Box, Grid, TextField } from "@material-ui/core";
import { Autocomplete as MuiAutocomplete } from "@material-ui/lab";
import { faPlus, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FieldArray, useFormikContext } from "formik";
import { useSelector } from "react-redux";

import { SlimButton } from "components/Button";
import { ButtonIcon } from "components/Button/FontAwesomeButton";
import { CollapseHeader, FormCollapse } from "components/Form";
import {
  Autocomplete,
  getOptionAsValue,
  LabelledControl,
  standardFilter,
  withNamespace,
} from "components/Form/FormikControls";

import { FinanceSubModalSection } from "constants/settings";

import { sectionHasErrors } from "lib/form";

import { getXeroTrackingCategories } from "selectors";

import { SectionToggleState, useSectionHelper } from "hooks";
import { useFieldValue } from "hooks/useFormik";

const MAX_TRACKING_CATEGORIES = 2;

interface TrackingCategoryRowProps {
  index: number;
  prefsFieldName: string;
  onRemove: () => void;
}

function TrackingCategoryRow({
  index,
  prefsFieldName,
  onRemove,
}: TrackingCategoryRowProps) {
  const { setFieldValue } = useFormikContext();
  const trackingCategories = useSelector(
    getXeroTrackingCategories,
  ) as XeroTrackingCategory[];

  const optionFieldName = `${prefsFieldName}[${index}].xeroTrackingCategoryOptionId`;
  const categoryFieldName = `${prefsFieldName}[${index}].xeroTrackingCategoryId`;

  const categoryId =
    (useFieldValue(categoryFieldName) as number | null) ?? null;

  const categoryOptions = trackingCategories.map(c => ({
    value: c.id,
    label: c.name,
  }));
  const trackingOptions = (
    trackingCategories.find(c => c.id === categoryId)?.options ?? []
  ).map(o => ({ value: o.id, label: o.name }));

  return (
    <Grid container spacing={2} item xs={12} alignItems="flex-start">
      <Grid item xs={12} md={5}>
        <LabelledControl
          label={`Tracking Category ${index + 1}`}
          name={`xeroTrackingCategory${index + 1}`}
          tooltip={undefined}
          required={false}
        >
          <MuiAutocomplete<{ value: number; label: string }>
            options={categoryOptions}
            value={categoryOptions.find(o => o.value === categoryId) ?? null}
            getOptionLabel={o => o.label}
            getOptionSelected={(option, value) =>
              typeof value !== "string" && option.value === value.value
            }
            onChange={(_, selected) => {
              const newCategoryId =
                typeof selected === "object" ? selected?.value ?? null : null;
              setFieldValue(categoryFieldName, newCategoryId);
              setFieldValue(optionFieldName, null);
            }}
            renderInput={params => <TextField {...params} />}
          />
        </LabelledControl>
      </Grid>
      <Grid item xs={12} md={5}>
        <Autocomplete
          name={optionFieldName}
          label={`Tracking Option ${index + 1}`}
          options={trackingOptions}
          getOptionValue={getOptionAsValue}
          filterOptions={standardFilter}
          disabled={!categoryId}
        />
      </Grid>
      <Grid item xs={12} md={2}>
        {/* Ghost spacer matching label height so button aligns with the inputs */}
        <Box style={{ fontSize: "0.9rem", visibility: "hidden" }}>&nbsp;</Box>
        <SlimButton
          onClick={onRemove}
          type="button"
          backgroundColor="deleteRed"
        >
          <ButtonIcon icon={faTrash} />
        </SlimButton>
      </Grid>
    </Grid>
  );
}

export const TrackingCategoriesSettings = ({
  sectionHelper,
  namespace: ns,
}: {
  sectionHelper: SectionToggleState;
  namespace: string;
}) => {
  const { isOpen, onToggle } = useSectionHelper(
    sectionHelper,
    FinanceSubModalSection.TRACKING_CATEGORIES,
  );

  const formikProps = useFormikContext();
  const { submitCount } = formikProps;

  const prefsFieldName = withNamespace(ns, "trackingCategoryPreferences");
  const preferences =
    (useFieldValue(prefsFieldName) as TrackingCategoryPreference[]) ?? [];

  const hasErrors =
    submitCount > 0 && sectionHasErrors(formikProps, [prefsFieldName]);

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      hasErrorBadge={hasErrors}
      header={<CollapseHeader title="Tracking Categories" />}
    >
      <FieldArray name={prefsFieldName}>
        {({ push, remove }) => (
          <>
            {preferences.map((_, index) => (
              <TrackingCategoryRow
                key={index}
                index={index}
                prefsFieldName={prefsFieldName}
                onRemove={() => remove(index)}
              />
            ))}
            {preferences.length < MAX_TRACKING_CATEGORIES && (
              <Grid item xs={12}>
                <SlimButton
                  onClick={() =>
                    push({
                      xeroTrackingCategoryOptionId: null,
                      xeroTrackingCategoryId: null,
                    })
                  }
                  type="button"
                >
                  <ButtonIcon icon={faPlus} />
                  &nbsp;Add Tracking Category
                </SlimButton>
              </Grid>
            )}
          </>
        )}
      </FieldArray>
    </FormCollapse>
  );
};
